import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiLock, FiCheck, FiAlertTriangle, FiArrowLeft } from "react-icons/fi";
import authService from "../../services/authService";
import {
  checkPasswordStrength,
  doPasswordsMatch,
  validatePasswordRequirements,
} from "../../utils/validationUtils";
import { handleNetworkError } from "../../utils/errorUtils";
import { AuthLayout, AuthButton, PasswordInput } from "./components";
import styles from "./Auth.module.css";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: "",
  });
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenError("Invalid password reset link. Please request a new one.");
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "password") {
      setPasswordStrength(checkPasswordStrength(value));
    }

    if (name === "confirmPassword" || name === "password") {
      setPasswordMismatch(false);
    }

    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!doPasswordsMatch(formData.password, formData.confirmPassword)) {
      setPasswordMismatch(true);
      return;
    }

    const passwordValidation = validatePasswordRequirements(formData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      await authService.resetPassword(token, { password: formData.password });

      setShowSuccess(true);

      setTimeout(() => {
        navigate("/signin");
      }, 3000);
    } catch (err) {
      const processedError = handleNetworkError(err);

      if (
        processedError.type === "VALIDATION_ERROR" &&
        err.response?.data?.reason === "TOKEN_EXPIRED"
      ) {
        setTokenError("This password reset link has expired. Please request a new one.");
      } else if (
        processedError.type === "VALIDATION_ERROR" &&
        err.response?.data?.reason === "TOKEN_INVALID"
      ) {
        setTokenError("Invalid password reset link. Please request a new one.");
      } else {
        setError(processedError.message);
      }

      setIsSubmitting(false);
    }
  };

  // Token Error State
  if (tokenError) {
    return (
      <AuthLayout
        icon={<FiAlertTriangle size={28} />}
        title="Link Expired"
      >
        <div className={`${styles['au-alert']} ${styles['au-alert--error']}`}>
          <span>{tokenError}</span>
        </div>

        <Link to="/forgot-password">
          <AuthButton variant="primary" fullWidth style={{ marginTop: '1rem' }}>
            Request New Link
          </AuthButton>
        </Link>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Link to="/signin" className={styles['au-back-link']}>
            <FiArrowLeft size={16} />
            <span>Back to Login</span>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Success State
  if (showSuccess) {
    return (
      <AuthLayout>
        <motion.div
          className={styles['au-success']}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className={styles['au-success__icon']}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FiCheck size={32} />
          </motion.div>
          <h2 className={styles['au-success__title']}>Password Reset!</h2>
          <p className={styles['au-success__text']}>Redirecting to login page...</p>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      icon={<FiLock size={28} />}
      title="Reset Password"
      subtitle="Create a new password for your account"
      footer={
        <Link to="/signin" className={styles['au-back-link']}>
          <FiArrowLeft size={16} />
          <span>Back to Login</span>
        </Link>
      }
    >
      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <form onSubmit={handleSubmit} className={styles['au-form']}>
        <PasswordInput
          label="New Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a new password"
          required
          disabled={isSubmitting}
          showStrength={true}
          strength={passwordStrength}
        />

        <PasswordInput
          label="Confirm New Password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm your new password"
          required
          disabled={isSubmitting}
          error={passwordMismatch ? "Passwords do not match" : ""}
        />

        <p style={{ 
          fontSize: '0.8125rem', 
          color: 'var(--au-text-muted)', 
          margin: '0.5rem 0' 
        }}>
          Password must be at least 8 characters with uppercase, lowercase, numbers, and special characters.
        </p>

        <AuthButton
          type="submit"
          variant="primary"
          fullWidth
          loading={isSubmitting}
        >
          {isSubmitting ? "Resetting Password..." : "Reset Password"}
        </AuthButton>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
